import type {
  GetInventoryByFactoryIdResponse,
  InventorySkuItem,
  InventoryVariantProduct,
  ItemProductByIdResponse,
  ProductInventoryItem,
  ProductProductById,
} from 'services/interface';

export type InventoryProduct = ProductInventoryItem;
export type InventoryVariant = InventoryVariantProduct;
export type InventorySku = InventorySkuItem;
export type InventoryListResponse = GetInventoryByFactoryIdResponse;
export type ProductByIdSkuItem = ItemProductByIdResponse;
export type ProductByIdDetail = ProductProductById;

/** ตัวกรองจัดการสต็อก — เลือกได้ทีละหนึ่ง */
export type InventoryStockFilterMode = 'all' | 'limited' | 'unlimited';

/** ตัวกรองสถานะการขายในคลัง */
export type InventorySaleFilterMode = 'all' | 'active' | 'inactive';

/** แท็บหลักในมุมมองคลัง — สต็อก SKU | มาสเตอร์สต็อก | ชุดเซ็ต */
export type InventoryWarehouseTab = 'sku' | 'master' | 'bundle';

export function isInventoryGeneralItem(
  item: ProductInventoryItem
): boolean {
  return item.type === 'general';
}

export function isInventorySkuItem(item: ProductInventoryItem): boolean {
  return item.type !== 'general';
}

/** ชื่อ + รายละเอียดในบรรทัดเดียว (general stock) */
export function formatGeneralStockDisplayName(
  name: string | null | undefined,
  description: string | null | undefined
): string {
  const title = (name ?? '').trim() || '—';
  const desc = (description ?? '').trim();
  if (!desc) return title;
  return `${title} — ${desc}`;
}

export function generalStockMatchesInventorySearch(
  name: string | null | undefined,
  description: string | null | undefined,
  remark: string | null | undefined,
  qLower: string
): boolean {
  if (!qLower) return true;
  const n = (name ?? '').toLowerCase();
  const d = (description ?? '').toLowerCase();
  const r = (remark ?? '').toLowerCase();
  return n.includes(qLower) || d.includes(qLower) || r.includes(qLower);
}

export function getInventorySkuItems(products: ProductInventoryItem[]): InventorySkuItem[] {
  return products.flatMap((p) => (p.variants ?? []).flatMap((v) => v.items ?? []));
}

export function productHasSkuMatchingStockFilter(
  product: ProductInventoryItem,
  mode: InventoryStockFilterMode
): boolean {
  if (mode === 'all') return true;
  const items = (product.variants ?? []).flatMap((v) => v.items ?? []);
  if (mode === 'limited') return items.some((s) => !!s.is_stock);
  return items.some((s) => !s.is_stock);
}

/** ค้นหาในคลัง — sku หรือ general ใน products[] */
export function inventoryItemMatchesSearch(
  item: ProductInventoryItem,
  qLower: string
): boolean {
  if (!qLower) return true;
  if (isInventoryGeneralItem(item)) {
    return generalStockMatchesInventorySearch(
      item.name,
      item.description,
      item.remark,
      qLower
    );
  }
  return productMatchesInventorySearch(item, qLower);
}

/** ค้นหาในคลัง — ชื่อสินค้า / ชื่อ SKU / รหัสสินค้า (ไม่สนตัวพิมพ์) */
export function productMatchesInventorySearch(
  product: ProductInventoryItem,
  qLower: string
): boolean {
  if (!qLower) return true;
  if ((product.name ?? '').toLowerCase().includes(qLower)) return true;
  const items = (product.variants ?? []).flatMap((v) => v.items ?? []);
  return items.some((it) => {
    const name = (it.name ?? '').toLowerCase();
    const code = (it.product_code ?? '').toLowerCase();
    return name.includes(qLower) || code.includes(qLower);
  });
}

export function productMatchesInventorySaleFilter(
  product: ProductInventoryItem,
  mode: InventorySaleFilterMode
): boolean {
  if (isInventoryGeneralItem(product)) return false;
  if (mode === 'all') return true;
  if (mode === 'active') return product.is_active === true;
  return product.is_active !== true;
}

/** อัปเดตแคช inventory จาก getById — เอาเฉพาะฟิลด์ที่ grid ใช้ ไม่ดึง merchants เข้าแคช */
export function inventorySkuFromProductByIdItem(item: ItemProductByIdResponse): InventorySkuItem {
  const { merchants: _m, merchant_id: _mid, ...rest } = item;
  return rest;
}

export function mergeProductInventoryFromById(
  inventory: ProductInventoryItem,
  fresh: ProductProductById
): ProductInventoryItem {
  const freshVariants = fresh.variants ?? [];
  const invVariants = inventory.variants ?? [];

  const nextVariants: InventoryVariantProduct[] = invVariants.map((invV) => {
    const freshV = freshVariants.find((fv) => fv.id === invV.id);
    if (!freshV) return invV;

    const freshItems = freshV.items ?? [];
    const nextItems: InventorySkuItem[] = (invV.items ?? []).map((invIt) => {
      const freshIt = freshItems.find((fi) => fi.id === invIt.id);
      return freshIt ? inventorySkuFromProductByIdItem(freshIt) : invIt;
    });

    return { ...invV, items: nextItems };
  });

  return {
    ...inventory,
    is_active: fresh.is_active ?? inventory.is_active,
    is_approve: fresh.is_approve ?? inventory.is_approve,
    variants: nextVariants,
  };
}
